import { useCallback, useEffect, useState } from "react";
import { FetchResult, useLazyQuery, useMutation } from "@apollo/client";
import { DocumentType } from "@api/graphql/generated/generated-types";
import {
  GET_COMPLIANCE_POLICIES,
  GetCompliancePoliciesData,
  GetCompliancePoliciesVars,
} from "../graphql/queries";
import {
  UPSERT_COMPLIANCE_POLICY,
  UpsertCompliancePolicyData,
  UpsertCompliancePolicyVars,
} from "../graphql/mutations";

export function useComplianceSettings(
  courierId: string | undefined,
  entityId: string | undefined,
) {
  const [requiredDocTypes, setRequiredDocTypes] = useState<DocumentType[]>([]);
  const [warnedDocTypes, setWarnedDocTypes] = useState<DocumentType[]>([]);
  const [excludedDocTypes, setExcludedDocTypes] = useState<DocumentType[]>([]);

  const [fetchPolicy, { loading: fetching }] = useLazyQuery<
    GetCompliancePoliciesData,
    GetCompliancePoliciesVars
  >(GET_COMPLIANCE_POLICIES, {
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      const policy = data.driverCompliancePolicies?.[0];
      setRequiredDocTypes(policy?.requiredDocumentTypes ?? []);
      setWarnedDocTypes(policy?.warnedDocumentTypes ?? []);
      setExcludedDocTypes(policy?.excludedDocumentTypes ?? []);
    },
  });

  const [upsertMutation, { loading: saving }] = useMutation<
    UpsertCompliancePolicyData,
    UpsertCompliancePolicyVars
  >(UPSERT_COMPLIANCE_POLICY);

  useEffect(() => {
    if (!courierId || !entityId) return;
    fetchPolicy({
      variables: { courierId, entityIds: [entityId] },
    });
  }, [courierId, entityId]);

  const save = useCallback((): Promise<FetchResult<UpsertCompliancePolicyData>> => {
    if (!courierId || !entityId)
      return Promise.resolve({});
    return upsertMutation({
      variables: {
        courierId,
        entityId,
        requiredDocumentTypes: requiredDocTypes,
        warnedDocumentTypes: warnedDocTypes,
        excludedDocumentTypes: excludedDocTypes,
      },
    });
  }, [courierId, entityId, requiredDocTypes, warnedDocTypes, excludedDocTypes, upsertMutation]);

  return {
    requiredDocTypes,
    setRequiredDocTypes,
    warnedDocTypes,
    setWarnedDocTypes,
    excludedDocTypes,
    setExcludedDocTypes,
    fetching,
    saving,
    save,
  };
}
